import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Search, FileText, Plus, Mail, Percent, Calendar,
  CheckCircle2, AlertCircle, Loader2
} from 'lucide-react';

export default function InstructorRosterPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const sectionId = searchParams.get('section_id');
  
  const [roster, setRoster] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(sectionId || '');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [noteData, setNoteData] = useState({ note_type: 'general', content: '', is_private: true });

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      fetchRoster();
    }
  }, [selectedSection]);

  const fetchSections = async () => {
    try {
      const res = await api.get('/instructor/dashboard');
      setSections(res.data.sections || []);
      if (!selectedSection && res.data.sections?.length > 0) {
        setSelectedSection(res.data.sections[0].id);
      }
    } catch (error) {
      toast.error('Failed to load sections');
    }
  };

  const fetchRoster = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/instructor/roster?section_id=${selectedSection}`);
      setRoster(res.data);
    } catch (error) {
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  const openNoteDialog = (student) => {
    setSelectedStudent(student);
    setNoteData({ note_type: 'general', content: '', is_private: true });
    setShowNoteDialog(true);
  };

  const saveNote = async () => {
    try {
      await api.post('/student-notes/', {
        section: selectedSection,
        learner: selectedStudent.learner_id,
        ...noteData
      });
      toast.success('Note saved');
      setShowNoteDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const filteredRoster = roster?.roster?.filter(student =>
    student.learner_name.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Class Roster</h1>
          <p className="text-muted-foreground">View and manage your students</p>
        </div>
      </div>

      {/* Section Selector */}
      <div className="flex items-center gap-4">
        <Select value={selectedSection} onValueChange={setSelectedSection}>
          <SelectTrigger className="w-[300px]">
            <SelectValue placeholder="Select a class" />
          </SelectTrigger>
          <SelectContent>
            {sections.map(section => (
              <SelectItem key={section.id} value={section.id}>
                {section.course_title} - {section.location}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search students..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Section Info */}
      {roster?.section && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-lg">{roster.section.course_title}</h2>
                <p className="text-sm text-muted-foreground">{roster.section.location}</p>
              </div>
              <Badge variant="outline" className="text-lg px-4 py-1">
                <Users className="h-4 w-4 mr-2" />
                {roster.total_students} Students
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Roster Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredRoster.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No students found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Parent Contact</TableHead>
                  <TableHead className="text-center">Attendance</TableHead>
                  <TableHead className="text-center">Avg Grade</TableHead>
                  <TableHead className="text-center">Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoster.map(student => (
                  <TableRow key={student.learner_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{student.learner_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Enrolled: {new Date(student.enrollment_date).toLocaleDateString()}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{student.parent_name}</p>
                        <a href={`mailto:${student.parent_email}`} className="text-xs text-primary hover:underline">
                          {student.parent_email}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {student.attendance_rate !== null ? (
                        <div className="flex flex-col items-center">
                          <span className={`font-medium ${
                            student.attendance_rate >= 80 ? 'text-green-600' :
                            student.attendance_rate >= 60 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {student.attendance_rate}%
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {student.classes_attended}/{student.total_classes}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {student.average_grade !== null ? (
                        <Badge variant={
                          student.average_grade >= 90 ? 'default' :
                          student.average_grade >= 70 ? 'secondary' : 'destructive'
                        }>
                          {student.average_grade}%
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{student.notes_count}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openNoteDialog(student)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.location.href = `mailto:${student.parent_email}`}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note for {selectedStudent?.learner_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Note Type</label>
              <Select 
                value={noteData.note_type} 
                onValueChange={(v) => setNoteData(prev => ({ ...prev, note_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="progress">Progress Update</SelectItem>
                  <SelectItem value="achievement">Achievement</SelectItem>
                  <SelectItem value="concern">Concern</SelectItem>
                  <SelectItem value="behavior">Behavior</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Note</label>
              <Textarea
                value={noteData.content}
                onChange={(e) => setNoteData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter your note..."
                rows={4}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="private"
                checked={noteData.is_private}
                onChange={(e) => setNoteData(prev => ({ ...prev, is_private: e.target.checked }))}
              />
              <label htmlFor="private" className="text-sm">Private note (only visible to instructors)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
            <Button onClick={saveNote}>Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
